// apps/web/src/lib/compras/cnpj.ts — Fase 13, Plano 17 (COMP-01, D-18).
// CNPJ no formulário de fornecedor: dígitos, validação dos dígitos verificadores, máscara e mensagens dos códigos do servidor.
// A autoridade é o servidor (CNPJ válido e único); aqui só se evita a ida e volta quando o erro é óbvio.

export const MENSAGEM_CNPJ_INVALIDO = 'Informe um CNPJ válido.';
export const MENSAGEM_RAZAO_OBRIGATORIA = 'Informe a razão social.';
export const MENSAGEM_FORNECEDOR_VERSAO = 'Este fornecedor foi alterado por outra pessoa. Recarregue e tente de novo.';

export function somenteDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

function digitoVerificador(base: string, pesos: readonly number[]): number {
  let soma = 0;
  for (let i = 0; i < pesos.length; i += 1) soma += Number(base[i]) * (pesos[i] as number);
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

const PESOS_PRIMEIRO = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const PESOS_SEGUNDO = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;

/** 14 dígitos (com ou sem máscara), não todos iguais, com os dois dígitos verificadores corretos. */
export function cnpjValido(valor: string): boolean {
  const digitos = somenteDigitos(valor);
  if (digitos.length !== 14 || /^(\d)\1{13}$/.test(digitos)) return false;
  const primeiro = digitoVerificador(digitos, PESOS_PRIMEIRO);
  if (primeiro !== Number(digitos[12])) return false;
  return digitoVerificador(digitos, PESOS_SEGUNDO) === Number(digitos[13]);
}

/** "00.000.000/0000-00"; texto que não tem 14 dígitos volta como veio (campo em digitação). */
export function formatarCnpj(valor: string): string {
  const d = somenteDigitos(valor);
  if (d.length !== 14) return valor;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Máscara progressiva para o campo de digitação (até 14 dígitos). */
export function mascararCnpjDigitando(valor: string): string {
  const d = somenteDigitos(valor).slice(0, 14);
  let saida = d.slice(0, 2);
  if (d.length > 2) saida += `.${d.slice(2, 5)}`;
  if (d.length > 5) saida += `.${d.slice(5, 8)}`;
  if (d.length > 8) saida += `/${d.slice(8, 12)}`;
  if (d.length > 12) saida += `-${d.slice(12)}`;
  return saida;
}

export interface ExtraErroFornecedor {
  /** FORNECEDOR_CNPJ_DUPLICADO: razão social do cadastro que já usa o CNPJ. */
  readonly razaoSocial?: string;
  /** FORNECEDOR_COM_PEDIDO_ABERTO: quantidade de pedidos em aberto. */
  readonly pedidosEmAberto?: number;
}

/**
 * Mensagem em português para os códigos de negócio do servidor; `null` para código desconhecido (a tela usa a mensagem
 * que o servidor devolveu). DUPLICADO e COM_PEDIDO_ABERTO só têm texto completo quando o dado extra é conhecido.
 */
export function mensagemErroFornecedor(codigo: string | null, extra: ExtraErroFornecedor = {}): string | null {
  switch (codigo) {
    case 'FORNECEDOR_CNPJ_INVALIDO':
      return MENSAGEM_CNPJ_INVALIDO;
    case 'FORNECEDOR_RAZAO_OBRIGATORIA':
      return MENSAGEM_RAZAO_OBRIGATORIA;
    case 'FORNECEDOR_CNPJ_DUPLICADO':
      return extra.razaoSocial ? `Já existe um fornecedor com este CNPJ (${extra.razaoSocial}).` : null;
    case 'FORNECEDOR_COM_PEDIDO_ABERTO':
      return extra.pedidosEmAberto !== undefined
        ? `Este fornecedor tem ${extra.pedidosEmAberto} pedido(s) em aberto. Conclua ou cancele antes de inativar.`
        : null;
    case 'FORNECEDOR_VERSAO_DESATUALIZADA':
      return MENSAGEM_FORNECEDOR_VERSAO;
    default:
      return null;
  }
}

/** "{N} fornecedores" (1 vira "1 fornecedor"). */
export function contagemFornecedores(total: number): string {
  return total === 1 ? '1 fornecedor' : `${total} fornecedores`;
}
